import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { DownloadIcon, FileArchiveIcon, Loader2Icon, ShieldAlertIcon } from 'lucide-react'
import { useId, useState } from 'react'

import { CORPORATE_KEY, corporateApi } from '@/api/corporate'
import { ApiError } from '@/api/errors'
import type { Language } from '@/api/types'
import { FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatDateOnly } from '@/lib/format'
import { localToday } from '@/lib/portal-home'

export const Route = createFileRoute('/_app/corporate/exporter')({
  component: ExportPage,
})

async function save(read: Promise<{ blob: Blob }>, name: string) {
  const { blob } = await read
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = name
  link.click()
  setTimeout(() => {
    URL.revokeObjectURL(url)
  }, 60_000)
}

/**
 * Exporter le livre (lot 45) : un PDF « Livre des minutes au <date> » — la
 * fiche de la société, les registres à la date, les résolutions adoptées, la
 * liste des pièces —, avec les pièces en option. Le registre de l'art. 18.1
 * n'y entre que si on le demande expressément.
 */
function ExportPage() {
  const ids = { date: useId(), docs: useId(), control: useId(), confirm: useId() }
  const [asOf, setAsOf] = useState(localToday())
  const [language, setLanguage] = useState<Language>('fr')
  const [documents, setDocuments] = useState(false)
  const [control, setControl] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [exportId, setExportId] = useState<string | null>(null)
  const start = useMutation({
    mutationFn: () =>
      corporateApi.startExport({
        as_of: asOf,
        language,
        include_documents: documents,
        include_control_register: control,
      }),
    onSuccess: (started) => {
      setExportId(started.id)
    },
  })
  const status = useQuery({
    queryKey: [...CORPORATE_KEY, 'export', exportId],
    queryFn: () => corporateApi.exportStatus(exportId ?? ''),
    enabled: exportId !== null,
    gcTime: 0,
    refetchInterval: (query) => (query.state.data?.status === 'pending' ? 2000 : false),
  })
  const download = useMutation({
    mutationFn: (kind: 'pdf' | 'zip') =>
      kind === 'pdf'
        ? save(corporateApi.exportPdf(exportId ?? ''), `livre-des-minutes-${asOf}.pdf`)
        : save(corporateApi.exportZip(exportId ?? ''), `livre-des-minutes-${asOf}-pieces.zip`),
  })
  const ready = status.data?.status === 'ready'
  const pending = start.isPending || status.data?.status === 'pending'
  return (
    <Frame className="max-w-2xl space-y-4" data-testid="exporter-livre">
      <h2 className="font-semibold">Exporter le livre</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor={ids.date}>Le livre au</Label>
          <Input
            id={ids.date}
            type="date"
            value={asOf}
            onChange={(event) => {
              setAsOf(event.target.value)
              setExportId(null)
            }}
          />
        </div>
        <fieldset className="space-y-1">
          <legend className="text-sm font-medium">Langue</legend>
          <div className="flex gap-3 pt-1.5 text-sm">
            {(['fr', 'en'] as const).map((value) => (
              <label key={value} className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name={ids.date}
                  className="accent-primary"
                  checked={language === value}
                  onChange={() => {
                    setLanguage(value)
                    setExportId(null)
                  }}
                />
                {value === 'fr' ? 'Français' : 'English'}
              </label>
            ))}
          </div>
        </fieldset>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <Checkbox
          id={ids.docs}
          checked={documents}
          onCheckedChange={(checked) => {
            setDocuments(checked === true)
            setExportId(null)
          }}
        />
        <Label htmlFor={ids.docs} className="font-normal">
          Joindre les pièces (dans le PDF et dans une archive ZIP)
        </Label>
      </div>
      <div className="space-y-2 rounded-md border border-warning/40 bg-warning/5 p-3 text-sm">
        <div className="flex items-center gap-2">
          <Checkbox
            id={ids.control}
            checked={control}
            onCheckedChange={(checked) => {
              setControl(checked === true)
              setConfirmed(false)
              setExportId(null)
            }}
          />
          <Label htmlFor={ids.control} className="font-normal">
            Inclure le registre des particuliers ayant un contrôle important (art. 18.1)
          </Label>
        </div>
        <p className="flex items-start gap-2 text-muted-foreground">
          <ShieldAlertIcon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          Ce registre contient des dates de naissance et des adresses. Il ne se remet qu’à qui y a
          droit — autorités fiscales, d’enquête ou de réglementation, sur demande. L’export est
          inscrit au journal du livre.
        </p>
        {control && (
          <div className="flex items-center gap-2" data-testid="export-confirmer-18-1">
            <Checkbox
              id={ids.confirm}
              checked={confirmed}
              onCheckedChange={(checked) => {
                setConfirmed(checked === true)
              }}
            />
            <Label htmlFor={ids.confirm} className="font-normal">
              Je confirme que ce registre est remis à qui y a droit.
            </Label>
          </div>
        )}
      </div>
      {start.error && (
        <FormError>
          {start.error instanceof ApiError ? start.error.message : 'L’export n’a pas pu démarrer.'}
        </FormError>
      )}
      <Button
        type="button"
        disabled={pending || (control && !confirmed)}
        data-testid="exporter-lancer"
        onClick={() => {
          start.mutate()
        }}
      >
        {pending ? <Loader2Icon className="animate-spin" /> : <FileArchiveIcon />}
        {pending ? 'Préparation du livre…' : 'Préparer l’export'}
      </Button>
      {status.data?.status === 'failed' && (
        <FormError>L’export a échoué : recommencez dans un instant.</FormError>
      )}
      {ready && (
        <div className="space-y-2 rounded-md border p-3 text-sm" data-testid="export-pret">
          <p>
            Le livre au {formatDateOnly(asOf)} est prêt. Les fichiers restent disponibles 7 jours.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={download.isPending}
              onClick={() => {
                download.mutate('pdf')
              }}
            >
              <DownloadIcon />
              Le livre (PDF)
            </Button>
            {documents && (
              <Button
                type="button"
                variant="outline"
                disabled={download.isPending}
                onClick={() => {
                  download.mutate('zip')
                }}
              >
                <DownloadIcon />
                Les pièces (ZIP)
              </Button>
            )}
          </div>
          {download.error && <FormError>Le fichier n’a pas pu être téléchargé.</FormError>}
        </div>
      )}
    </Frame>
  )
}
